import { ThreeEvent } from '@react-three/fiber';
import { DirectPointDragHandlers } from 'hooks/use-direct-point-drag';
import { useCallback, useRef } from 'react';
import { Vector3 } from 'three';
import {
  DuctPoint3DGeometry,
  isDuctPointDragUnchanged,
  resolveDuctPointDrag
} from 'utils/3d-duct-geometry';
import { PointPx } from 'utils/duct-runs';
import { TransformControlsInteractionState } from 'utils/transform-controls';
import DuctPoint3D from './duct-point-3d';
import TranslateGizmo3D from './translate-gizmo-3d';

type DuctPointGizmoProps = {
  pointGeometry: DuctPoint3DGeometry;
  yOffset: number;
  runWorldHeight?: number;
  color: string;
  radius?: number;
  opacity?: number;
  clampPx: (proposedPx: PointPx) => PointPx | null;
  onDragStart: () => void;
  onDragChange: (px: PointPx) => void;
  onDragEnd: (px: PointPx | null) => void;
  onContextMenu?: (event: ThreeEvent<PointerEvent>) => void;
  dragHandlers?: DirectPointDragHandlers;
  onInteractionChange?: (state: TransformControlsInteractionState) => void;
};

export default function DuctPointGizmo({
  pointGeometry,
  yOffset,
  runWorldHeight,
  color,
  radius,
  opacity = 1,
  clampPx,
  onDragStart,
  onDragChange,
  onDragEnd,
  onContextMenu,
  dragHandlers,
  onInteractionChange
}: DuctPointGizmoProps) {
  const dragStartPxRef = useRef(pointGeometry.px);
  const pointGeometryRef = useRef(pointGeometry);

  pointGeometryRef.current = pointGeometry;

  const handleDragStart = useCallback(() => {
    dragStartPxRef.current = pointGeometryRef.current.px;
    onDragStart();
  }, [onDragStart]);

  const handleDragChange = useCallback(
    (anchorPosition: Vector3) => {
      onDragChange(
        resolveDuctPointDrag(anchorPosition, yOffset, clampPx, runWorldHeight)
      );
    },
    [yOffset, runWorldHeight, clampPx, onDragChange]
  );

  const handleDragEnd = useCallback(
    (anchorPosition: Vector3 | null) => {
      if (!anchorPosition) {
        onDragEnd(null);
        return;
      }

      const px = resolveDuctPointDrag(
        anchorPosition,
        yOffset,
        clampPx,
        runWorldHeight
      );
      const unchanged = isDuctPointDragUnchanged(dragStartPxRef.current, px);

      onDragEnd(unchanged ? null : px);
    },
    [yOffset, runWorldHeight, clampPx, onDragEnd]
  );

  return (
    <TranslateGizmo3D
      position={[pointGeometry.x, pointGeometry.y, pointGeometry.z]}
      showY={false}
      onDragStart={handleDragStart}
      onDragChange={handleDragChange}
      onDragEnd={handleDragEnd}
      onInteractionChange={onInteractionChange}
    >
      <DuctPoint3D
        color={color}
        radius={radius}
        opacity={opacity}
        selected
        onContextMenu={onContextMenu}
        dragHandlers={dragHandlers}
      />
    </TranslateGizmo3D>
  );
}
